import { z } from 'zod';
import { ApiResponseError } from '../helpers/fetch-wrapper';
import { zGoSlice } from '../helpers/fix-go-types';
import type { Companion } from './uploader-connection';
import type { Vendor } from './vendor';

// --- Vendor instructions (matches Go VendorInstructions + FormatInstructions) ---

export const FormatInstructionsSchema = z.object({
  format: z.string(),
  label: z.string(),
  content: z.string(),
});

export type FormatInstructions = z.infer<typeof FormatInstructionsSchema>;

export const VendorInstructionsSchema = z.object({
  vendor_normalized_name: z.string(),
  // `_default/_vendor.md` ships `display_name: ""`, and any unrecognized vendor
  // key falls back to `_default`, so "" is a real response value here.
  display_name: z.string(),
  portal_url: z.string(),
  formats: z.array(FormatInstructionsSchema),
});

export type VendorInstructions = z.infer<typeof VendorInstructionsSchema>;

// --- Processing result (matches receive-invoices-sync JSON response) ---

export const ReceivingFileSchema = z.object({
  name: z.string(),
  // Left permissive on purpose: the API deliberately sends "" here.
  // ReceivingSession.WebView() blanks the LPOS import file's base64 payload on
  // every web response and WebSocket push (the bytes are for DynamoDB and the
  // companion, and a real one 413s API Gateway's 128 KB frame limit), keeping
  // the key so the shape stays stable. Yup's `.required()` rejected "" as
  // missing, which failed the WHOLE session (!4530); `z.string()` accepts it,
  // so do not "tighten" this to `.min(1)`.
  content: z.string(),
  content_hash: z.string(),
  // `Sources []string` carries no omitempty (models.ReceivingOutputFile), so a
  // nil slice would reach us as null. Not a live bug — the only construction
  // site (pkg/invoicereceiver/receiver.go) never yields nil — but the guard
  // belongs here rather than in whichever screen reads it next.
  sources: zGoSlice(z.string()),
});

export type ReceivingFile = z.infer<typeof ReceivingFileSchema>;

export const ProductOrderSchema = z.object({
  raw_item_code: z.string(),
  item_code: z.string(),
  suggested_item_code: z.string(),
  barcode: z.string(),
  brand: z.string(),
  description: z.string(),
  size: z.string(),
  case_qty: z.number(),
  cases_ordered: z.number(),
  units_ordered: z.number(),
  sales_price: z.number(),
  // Owning LiquorPOS vendor of the matched product; '' on unmatched rows and on
  // results persisted before multi-vendor receiving. .default('') is required:
  // persisted results rehydrate from IndexedDB raw, bypassing schema parsing.
  vendor_id: z.string().default(''),
});

export type ProductOrder = z.infer<typeof ProductOrderSchema>;

export const UNKNOWN_REASON_DUPLICATE_ITEM_CODE = 'duplicate_item_code';

export const ReceivingConflictSchema = z.object({
  product_id: z.string(),
  brand: z.string(),
  description: z.string(),
  size: z.string(),
  vendor_id: z.string().default(''),
});

export type ReceivingConflict = z.infer<typeof ReceivingConflictSchema>;

// Unknown rows extend a parsed order with why they didn't match. Both fields
// use .default() because processing results are persisted on the session and
// rehydrated from cache raw, bypassing schema parsing — a bare required field
// would throw on sessions created before these fields existed. `conflicts` is
// omitempty on the Go side (absent for no_match), so undefined → [].
export const UnknownProductSchema = ProductOrderSchema.extend({
  unknown_reason: z.string().default(''),
  conflicts: z.array(ReceivingConflictSchema).default([]),
});

export type UnknownProduct = z.infer<typeof UnknownProductSchema>;

// Splits unknown rows into item-code collisions vs. everything else. `noMatch`
// is the negation so rows from older sessions (empty reason) fall through to it.
export function partitionUnknownProducts(unknowns: UnknownProduct[]) {
  return {
    duplicateItemCodes: unknowns.filter(
      (u) => u.unknown_reason === UNKNOWN_REASON_DUPLICATE_ITEM_CODE
    ),
    noMatch: unknowns.filter((u) => u.unknown_reason !== UNKNOWN_REASON_DUPLICATE_ITEM_CODE),
  };
}

export const ProductOrderMatchSchema = z.object({
  product: ProductOrderSchema,
  matched_by: z.string(),
});

export type ProductOrderMatch = z.infer<typeof ProductOrderMatchSchema>;

export const UnparsedRowSchema = z.object({
  filename: z.string(),
  row: z.number(),
  // Raw CSV cells: blank cells are "" (not missing data), so the element
  // validator must accept "". This was the Mary's Liquor drop bug.
  //
  // zGoSlice, not z.array: `Data []string` carries no omitempty, and
  // `append([]string(nil), row...)` is nil for an empty source row. A dozen
  // parsers build those rows, so a null here is not provably impossible — and
  // rejecting it would fail the whole processing result over one unparsed row.
  data: zGoSlice(z.string()),
  err: z.string(),
});

export type UnparsedRow = z.infer<typeof UnparsedRowSchema>;

// One invoice file's extracted totals alongside the totals the file printed
// for itself, where it printed any. Mirrors Go models.ReceivingInvoiceTotals.
// The backend writes one entry per file whenever the file parsed, mismatch or
// not, which is what lets us tell "this vendor's format prints no total" from
// "this session predates the field" — both would otherwise be an absent entry.
//
// Every printed_* field is a Go pointer, so `null` on the wire for any figure
// the document doesn't print. No vendor prints a line count, which is why only
// the extracted side has one.
export const InvoiceTotalsSchema = z.object({
  filename: z.string(),
  printed_cases: z.number().nullable().default(null),
  printed_units: z.number().nullable().default(null),
  printed_amount: z.number().nullable().default(null),
  extracted_lines: z.number().default(0),
  extracted_cases: z.number().default(0),
  extracted_units: z.number().default(0),
  extracted_amount: z.number().default(0),
});

export type InvoiceTotals = z.infer<typeof InvoiceTotalsSchema>;

// When Go serializes the processing result, every field is always present —
// nil slices/maps/pointers become JSON `null`. So a nullable-but-present field
// is correct for each one here, and `null` stays a real value distinct from an
// empty array: it means "nothing to show", not "nothing matched" (ORD-399).
// zGoSlice would collapse that distinction and render an unverified invoice as
// a verified-empty one, which is why none of these use it.
export const ProcessingResultSchema = z.object({
  receiving_file: ReceivingFileSchema.nullable(),
  parse_errors: z.record(z.string(), z.string()).nullable(),
  matched: z.array(ProductOrderMatchSchema).nullable(),
  not_parsed_rows: z.array(UnparsedRowSchema).nullable(),
  unknown_products: z.array(UnknownProductSchema).nullable(),
  // ORD-399. `.default(null)` rather than a bare nullable: results persisted
  // before ORD-392 shipped carry no such key, and they rehydrate from
  // IndexedDB raw, bypassing schema parsing — same reason `vendor_id` and
  // `conflicts` above carry defaults.
  invoice_totals: z.array(InvoiceTotalsSchema).nullable().default(null),
});

export type ProcessingResult = z.infer<typeof ProcessingResultSchema>;

// Money compares with a half-cent epsilon, matching the Go checksum tests
// (docs/superpowers/specs/2026-08-20-ord-392-invoice-totals-cross-check-design.md
// §5). SGWS rounds per-bottle prices to cents, so a summed total can land a
// fraction of a cent from the printed one with nothing actually wrong.
// Quantities compare exactly — there is no such rounding on a case count.
const MONEY_EPSILON = 0.005;

// The four figures the Review table compares, in the order it shows them.
// `lines` is only ever populated on the read side: no vendor prints a line
// count, which is the whole reason the extracted side carries one.
export type InvoiceFigures = {
  amount: number | null;
  cases: number | null;
  units: number | null;
  lines: number | null;
};

export const INVOICE_FIGURES = ['amount', 'cases', 'units', 'lines'] as const;

export type InvoiceFigure = (typeof INVOICE_FIGURES)[number];

// `matches`   — the file printed totals and we agree with all of them.
// `differs`   — the file printed totals and at least one disagrees. Warn
//               loudly, never block: a false positive must not strand a
//               delivery mid-receiving.
// `unverified` — the file prints no totals of its own (LDF, RNDC-OK, DSD,
//               BEES, generic CSV — most of the CSV volume). Deliberately not
//               a warning: it would fire on the same vendors every day and
//               train owners to ignore the region. The absent Invoice row is
//               the whole signal; the read total alone is what makes the file
//               checkable against the paper invoice.
export type InvoiceTotalsVerdict = 'matches' | 'differs' | 'unverified';

export type InvoiceTotalsSummary = {
  filename: string;
  verdict: InvoiceTotalsVerdict;
  // What we got out of the file.
  read: InvoiceFigures;
  // What the file said about itself; null per figure it doesn't print.
  printed: InvoiceFigures;
  // printed minus read, per figure, and null wherever there is nothing to
  // compare or the two agree. A positive value means the invoice claims more
  // than we found, which is the direction that under-receives stock.
  difference: InvoiceFigures;
  // Rows of this file the parser couldn't read. They are excluded from the
  // read totals on purpose (pkg/invoicereceiver/receiver.go), so they are the
  // one thing in the session that can explain a shortfall.
  unreadRows: number;
};

const epsilonFor = (figure: InvoiceFigure) => (figure === 'amount' ? MONEY_EPSILON : 0);

// A file counts as checkable if it printed ANY figure, not just the amount:
// Empire PDFs set Total Cases and Invoice Amount under independent conditions,
// and both SGWS readers parse each label separately, so cases-without-amount is
// a real wire state. Keying only on the amount would let those through
// unflagged.
function compare(read: InvoiceFigures, printed: InvoiceFigures) {
  const difference: InvoiceFigures = { amount: null, cases: null, units: null, lines: null };
  let checkable = false;

  for (const figure of INVOICE_FIGURES) {
    const p = printed[figure];
    const r = read[figure];
    if (p == null || r == null) {
      continue;
    }

    checkable = true;
    const delta = p - r;
    if (Math.abs(delta) > epsilonFor(figure)) {
      difference[figure] = delta;
    }
  }

  const differs = INVOICE_FIGURES.some((figure) => difference[figure] != null);

  return {
    difference,
    verdict: (checkable ? (differs ? 'differs' : 'matches') : 'unverified') as InvoiceTotalsVerdict,
  };
}

// One summary per invoice file in the session, ready to render. Returns []
// for a session processed before ORD-392 shipped, where the field is absent —
// those render nothing rather than a total we can't stand behind.
export function summarizeInvoiceTotals(result: ProcessingResult): InvoiceTotalsSummary[] {
  const unreadByFile = new Map<string, number>();
  for (const row of result.not_parsed_rows ?? []) {
    unreadByFile.set(row.filename, (unreadByFile.get(row.filename) ?? 0) + 1);
  }

  return (result.invoice_totals ?? []).map((totals) => {
    const read: InvoiceFigures = {
      amount: totals.extracted_amount,
      cases: totals.extracted_cases,
      units: totals.extracted_units,
      lines: totals.extracted_lines,
    };
    const printed: InvoiceFigures = {
      amount: totals.printed_amount ?? null,
      cases: totals.printed_cases ?? null,
      units: totals.printed_units ?? null,
      lines: null,
    };

    return {
      filename: totals.filename,
      read,
      printed,
      unreadRows: unreadByFile.get(totals.filename) ?? 0,
      ...compare(read, printed),
    };
  });
}

// --- Session state (matches Go models.ReceivingSessionState, post-MR-1a) ---

export const receivingSessionStates = [
  'selecting_companion', // NEW — first state in the flow
  'selecting_vendor',
  'get_invoices',
  'processing',
  'review',
  'sending_to_lpos',
  'paused_lpos_busy', // net-new: write blocked or LPOS running own receiving
  'waiting_for_lpos',
  'complete',
  'failed',
  'cancelled', // frontend-only sentinel from Phase A (ReceivingStateCancelled)
] as const;

export type ReceivingSessionState = (typeof receivingSessionStates)[number];

// Pre-Confirm states — those that precede POST /confirm (the point of no clean
// return, Invariant 5). The single TS source of truth for Cancel reachability:
// canCancelSession below and the pause screen both derive from it. Keep in sync
// with the Go mirror models.PreConfirmReceivingStates
// (lambdas/api/models/receiving_session.go).
export const PRE_CONFIRM_STATES: ReadonlySet<string> = new Set<ReceivingSessionState>([
  'selecting_companion',
  'selecting_vendor',
  'get_invoices',
  'processing',
  'review',
]);

// States from which the DELETE /cancel endpoint (clean rewind) is allowed.
// Mirrors the backend CancelReachable guard — keep in sync. A `failed` session
// is NOT cancelable: Cancel refuses terminal records (they age out via TTL), and
// the resolution path for a failure is Retry/Abandon from the session page.
// For paused_lpos_busy, Cancel is valid only when paused_from is pre-Confirm (the
// write hadn't started); post-Confirm pauses offer Abandon, not Cancel.
export const canCancelSession = (s: ReceivingSessionState, pausedFrom?: string): boolean => {
  if (PRE_CONFIRM_STATES.has(s)) {
    return true;
  }

  if (s === 'paused_lpos_busy') {
    return pausedFrom != null && PRE_CONFIRM_STATES.has(pausedFrom);
  }

  return false;
};

// /cancel answers 409 for several unrelated reasons and the client recovers
// from each differently, so the body's `code` is what tells them apart —
// mirrors SessionHeldByOtherTabCode in lambdas/api/api.go. Never infer the
// cause from the message text: the prose is copy, not contract.
const SESSION_HELD_BY_OTHER_TAB_CODE = 'SESSION_HELD_BY_OTHER_TAB';

/**
 * True when cancel was refused because another tab still has the session open
 * and connected — a normal outcome the user resolves themselves, not a defect.
 *
 * Lives here beside canCancelSession because it is domain knowledge about the
 * wire contract, not transport: it needs no FetchWrapper and mirrors
 * SessionHeldByOtherTabCode, exactly as canCancelSession mirrors the backend's
 * CancelReachable. services/receiving is transport-only, which is what lets
 * every router suite stub it wholesale (services/__mocks__/receiving.ts) — a
 * predicate defined there would be stubbed out of the very cancel path under
 * test, which is how the wrong home first showed up.
 *
 * Callers show a warning and skip error reporting for it, so this stays strict:
 * status *and* code together. Anything else gets reported normally.
 */
export const isSessionHeldByOtherTab = (error: unknown): boolean =>
  error instanceof ApiResponseError &&
  error.status === 409 &&
  error.body?.code === SESSION_HELD_BY_OTHER_TAB_CODE;

// /upload-url answers 409 for the in-session duplicate-file guard alongside an
// unrelated cause (no vendor selected yet), so the body's `code` is what tells
// them apart — mirrors DuplicateFileInSessionCode in lambdas/api/api.go. Never
// infer the cause from the message text, same discipline as
// isSessionHeldByOtherTab above.
const DUPLICATE_FILE_IN_SESSION_CODE = 'DUPLICATE_FILE_IN_SESSION';

/**
 * True when an upload was refused because the identical bytes are already
 * present in this session (ORD-451). Not a retryable failure — the file is
 * already here — so callers treat it as informational rather than as an
 * upload error, and do not let it block Process.
 */
export const isDuplicateFileInSession = (error: unknown): error is ApiResponseError =>
  error instanceof ApiResponseError &&
  error.status === 409 &&
  error.body?.code === DUPLICATE_FILE_IN_SESSION_CODE;

// States in which the given field is populated on the wire. Used by the
// session schema to turn requirements on/off as the state machine progresses.
// `failed` and `cancelled` can occur from any prior state, so their field
// shape depends on what was set before — we don't enforce those strictly.
const STATES_WITH_VENDOR: ReceivingSessionState[] = [
  'get_invoices',
  'processing',
  'review',
  'sending_to_lpos',
  'waiting_for_lpos',
  'complete',
];

const STATES_WITH_PROCESSING_RESULT: ReceivingSessionState[] = [
  'review',
  'sending_to_lpos',
  'waiting_for_lpos',
  'complete',
];

export const receivingSessionFileSources = ['web_upload', 'companion_detected'] as const;

export type ReceivingSessionFileSource = (typeof receivingSessionFileSources)[number];

// Mirrors Go models.ReceivingSessionFile.
export const ReceivingSessionFileSchema = z.object({
  filename: z.string(),
  source: z.enum(receivingSessionFileSources),
  s3_key: z.string(),
  size: z.number(),
  // ORD-451. Both `omitempty` on the Go side: sha256 is absent on a
  // companion-detected file and on anything uploaded before this shipped;
  // fingerprint is absent until the session has been processed, and on a file
  // that failed to parse.
  sha256: z.string().optional(),
  fingerprint: z.string().optional(),
});

export type ReceivingSessionFile = z.infer<typeof ReceivingSessionFileSchema>;

// Yup's `.required()` rejected '' alongside absent and null, and the
// state-conditional requirements below were written against that. Zod has no
// equivalent built in, so the rule is spelled out once here rather than
// quietly loosened field by field.
const isBlank = (value: string | null | undefined): boolean => value == null || value === '';

export const receivingDuplicateKinds = ['identical_bytes', 'same_content'] as const;

export type ReceivingDuplicateKind = (typeof receivingDuplicateKinds)[number];

// Mirrors Go models.ReceivingDuplicate — one file in this session that was
// already received in another one, inside the 7-day window.
export const ReceivingDuplicateSchema = z.object({
  filename: z.string(),
  kind: z.enum(receivingDuplicateKinds),
  matched_session_id: z.string(),
  matched_filename: z.string(),
  received_at: z.string(),
});

export type ReceivingDuplicate = z.infer<typeof ReceivingDuplicateSchema>;

// Mirrors Go models.ReceivingSession. Intentionally NO `created_at` — the Go
// struct only carries `updated_at` and `ttl`.
//
// Which fields are present depends on the session state; fields become
// required as the state machine progresses. Each of those is declared optional
// on the object and then required by the `superRefine` below, because Zod has
// no cross-field `.when()`. One refinement carrying all four rules beats a
// discriminated union over the 11 states, whose predicates overlap via
// STATES_WITH_VENDOR / STATES_WITH_PROCESSING_RESULT and would have to be
// restated per branch.
//
// `processing_result` needs no Yup.lazy equivalent: Zod never casts an absent
// object to `{}`, so an absent result cannot cascade into the inner
// required-field checks the way it did under Yup.
export const ReceivingSessionSchema = z
  .object({
    store_id: z.string(),
    session_id: z.string(),
    state: z.enum(receivingSessionStates),
    machine_id: z.string().optional(),
    vendor_id: z.string().optional(),
    // The full vendor set. Empty on sessions written before multi-vendor
    // receiving — those carry only the scalar vendor_id, and live up to 24h.
    vendor_ids: z.array(z.string()).default([]),
    files: z.array(ReceivingSessionFileSchema).optional(),
    processing_result: ProcessingResultSchema.nullish(),
    error: z.string().optional(),
    failed_from: z.string().optional(),

    // Net-new fields (Rev 3 / B16): timestamps + pause cause + content hash.
    // All optional on the wire — absent when state hasn't reached that point yet.
    paused_from: z.string().optional(),
    paused_since: z.string().optional(),
    waiting_since: z.string().optional(),
    content_hash: z.string().optional(),
    // ORD-451. duplicates defaults to [] rather than being left optional: a session
    // written before this shipped carries no such key, and it rehydrates from
    // IndexedDB raw, bypassing Zod casting — same reason vendor_ids has one.
    received_at: z.string().optional(),
    duplicates: z.array(ReceivingDuplicateSchema).default([]),

    active_viewer_tab_id: z.string().optional(),
    updated_at: z.string(),
    ttl: z.number(),
  })
  .superRefine((session, ctx) => {
    const requireField = (field: 'machine_id' | 'vendor_id' | 'error' | 'failed_from') => {
      if (isBlank(session[field])) {
        ctx.addIssue({ code: 'custom', message: `${field} is a required field`, path: [field] });
      }
    };

    if (
      session.state !== 'selecting_companion' &&
      session.state !== 'cancelled' &&
      session.state !== 'failed'
    ) {
      requireField('machine_id');
    }

    if (STATES_WITH_VENDOR.includes(session.state)) {
      requireField('vendor_id');
    }

    if (
      STATES_WITH_PROCESSING_RESULT.includes(session.state) &&
      session.processing_result == null
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'processing_result is required in this state',
        path: ['processing_result'],
      });
    }

    if (session.state === 'failed') {
      requireField('error');
      requireField('failed_from');
    }
  });

export type ReceivingSession = z.infer<typeof ReceivingSessionSchema>;

// Vendor objects a session actually spans: the vendor_ids multi-vendor set
// when present, falling back to the single legacy vendor_id — sessions
// written before ORD-395 multi-vendor receiving carry only the scalar field.
// Single source of truth for the fallback rule, used by both the review
// header's vendor name/count and the Vendor provenance column's id→name map.
export function sessionVendors(
  session: ReceivingSession | null | undefined,
  vendors: Vendor[]
): Vendor[] {
  if (!session) {
    return [];
  }

  const ids = session.vendor_ids?.length
    ? session.vendor_ids
    : session.vendor_id
      ? [session.vendor_id]
      : [];

  return ids
    .map((id) => vendors.find((v) => v.vendor_id === id))
    .filter((v): v is Vendor => v !== undefined);
}

// A companion can't take on a new or resumed session while it already has an
// active session assigned, or while its disk still holds a pending LPOS import
// file (rcv_blocked). Single source of truth for "is this companion free?",
// used by both the receiving list page (enabling "Start new receiving" /
// Resume) and the in-session companion picker — keep the two in sync.
export function makeCompanionBusyPredicate(
  activeSessions: ReceivingSession[]
): (companion: Companion) => boolean {
  const machineIdsWithSession = new Set(
    activeSessions.map((s) => s.machine_id).filter((id): id is string => id != null && id !== '')
  );

  return (companion) =>
    machineIdsWithSession.has(companion.uploader.machine_id) ||
    companion.uploader.rcv_blocked === true;
}
